import { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";

import AddTaskModal from "../../components/tasks/AddTaskModal";
import AssignTechnicianModal from "../../components/tasks/AssignTechnicianModal";
import { fetchTasks } from "../../api/tasks";
import { fetchTechnicians } from "../../api/users";
import { mapTaskFromApi } from "../../utils/taskMapper";
import TableLoader from "../../components/loaders/TableLoader";

const STATUS_OPTIONS = [
  { label: "All", value: "All" },
  { label: "Pending", value: "PENDING_ACCEPTANCE" },
  { label: "In Progress", value: "IN_PROGRESS" },
  { label: "Completed", value: "COMPLETED" },
  { label: "Overdue", value: "OVERDUE" },
];

const PAGE_SIZE_OPTIONS = [10, 20, 50];

function formatDate(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function prettyStatus(status) {
  return status?.replaceAll("_", " ") || "UNKNOWN";
}

function statusStyles(status) {
  switch (status) {
    case "COMPLETED":
      return "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-300 dark:ring-emerald-500/20";
    case "IN_PROGRESS":
      return "bg-blue-50 text-blue-700 ring-1 ring-blue-200 dark:bg-blue-500/10 dark:text-blue-300 dark:ring-blue-500/20";
    case "OVERDUE":
      return "bg-red-50 text-red-700 ring-1 ring-red-200 dark:bg-red-500/10 dark:text-red-300 dark:ring-red-500/20";
    case "PENDING_ACCEPTANCE":
    default:
      return "bg-amber-50 text-amber-700 ring-1 ring-amber-200 dark:bg-amber-500/10 dark:text-amber-300 dark:ring-amber-500/20";
  }
}

function buildPageNumbers(currentPage, totalPages) {
  const pages = [];
  const add = (value) => {
    if (!pages.includes(value)) pages.push(value);
  };

  add(1);
  for (let i = currentPage - 1; i <= currentPage + 1; i += 1) {
    if (i > 1 && i < totalPages) add(i);
  }
  if (totalPages > 1) add(totalPages);

  const finalPages = [];
  for (let i = 0; i < pages.length; i += 1) {
    finalPages.push(pages[i]);
    if (pages[i + 1] && pages[i + 1] - pages[i] > 1) {
      finalPages.push("...");
    }
  }

  return finalPages;
}

function KpiCard({ label, value, icon, tone = "default", subtitle }) {
  const toneMap = {
    default:
      "bg-white border-slate-200 dark:bg-slate-900 dark:border-slate-800",
    amber:
      "bg-amber-50/70 border-amber-200 dark:bg-amber-500/10 dark:border-amber-500/20",
    blue:
      "bg-blue-50/70 border-blue-200 dark:bg-blue-500/10 dark:border-blue-500/20",
    emerald:
      "bg-emerald-50/70 border-emerald-200 dark:bg-emerald-500/10 dark:border-emerald-500/20",
    red:
      "bg-red-50/70 border-red-200 dark:bg-red-500/10 dark:border-red-500/20",
  };

  return (
    <div
      className={`rounded-2xl border p-4 shadow-sm transition ${toneMap[tone]}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">
            {label}
          </p>
          <p className="mt-2 text-2xl font-semibold tracking-tight text-slate-900 dark:text-white">
            {value}
          </p>
          {subtitle && (
            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
              {subtitle}
            </p>
          )}
        </div>

        <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-slate-100 text-lg dark:bg-slate-800">
          {icon}
        </div>
      </div>
    </div>
  );
}

export default function Tasks() {
  const navigate = useNavigate();

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [openAddTask, setOpenAddTask] = useState(false);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [assignTaskId, setAssignTaskId] = useState(null);
  const [technicians, setTechnicians] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const loadTasks = async (status = "All") => {
    try {
      setLoading(true);
      const data = await fetchTasks(status);
      setTasks((data || []).map(mapTaskFromApi));
    } catch (err) {
      console.error("FETCH TASKS FAILED", err);
      setTasks([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTechnicians().then(setTechnicians).catch(console.error);
  }, []);

  useEffect(() => {
    loadTasks(statusFilter);
  }, [statusFilter]);

  useEffect(() => {
    setPage(1);
  }, [search, statusFilter, pageSize]);

  const sortedTasks = useMemo(
    () =>
      [...tasks].sort(
        (a, b) => new Date(b.assignedDate) - new Date(a.assignedDate)
      ),
    [tasks]
  );

  const filteredTasks = useMemo(() => {
    let filtered = sortedTasks;

    if (search.trim()) {
      const q = search.toLowerCase();
      filtered = filtered.filter((t) => {
        const id = String(t.id || "").toLowerCase();
        const technician = String(t.technician?.name || "").toLowerCase();
        const customer = String(t.customer?.name || "").toLowerCase();
        const description = String(t.description || "").toLowerCase();
        const title = String(t.title || "").toLowerCase();
        const tractor = String(t.tractor?.model || "").toLowerCase();
        const serial = String(t.tractor?.serial || "").toLowerCase();

        return (
          id.includes(q) ||
          technician.includes(q) ||
          customer.includes(q) ||
          description.includes(q) ||
          title.includes(q) ||
          tractor.includes(q) ||
          serial.includes(q)
        );
      });
    }

    if (statusFilter !== "All") {
      filtered = filtered.filter((t) => t.status === statusFilter);
    }

    return filtered;
  }, [search, statusFilter, sortedTasks]);

  const stats = useMemo(() => {
    const total = tasks.length;
    const pending = tasks.filter((t) => t.status === "PENDING_ACCEPTANCE").length;
    const inProgress = tasks.filter((t) => t.status === "IN_PROGRESS").length;
    const completed = tasks.filter((t) => t.status === "COMPLETED").length;
    const overdue = tasks.filter((t) => t.status === "OVERDUE").length;
    const unassigned = tasks.filter(
      (t) => t.technician?.name === "Unassigned"
    ).length;

    return { total, pending, inProgress, completed, overdue, unassigned };
  }, [tasks]);

  const totalPages = Math.max(1, Math.ceil(filteredTasks.length / pageSize));
  const safePage = Math.min(page, totalPages);

  const paginatedTasks = useMemo(() => {
    const start = (safePage - 1) * pageSize;
    return filteredTasks.slice(start, start + pageSize);
  }, [filteredTasks, safePage, pageSize]);

  const visiblePageNumbers = useMemo(
    () => buildPageNumbers(safePage, totalPages),
    [safePage, totalPages]
  );

  const activeFilterLabel =
    STATUS_OPTIONS.find((option) => option.value === statusFilter)?.label ||
    "All";

  return (
    <div className="space-y-6 text-slate-900 dark:text-slate-100">
      {/* Premium header */}
      <section className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-[0_12px_40px_rgba(15,23,42,0.07)] dark:border-slate-800 dark:bg-slate-900 dark:shadow-[0_12px_40px_rgba(0,0,0,0.32)]">
        <div className="border-b border-slate-200 bg-gradient-to-r from-slate-50 via-white to-slate-50 px-6 py-6 dark:border-slate-800 dark:from-slate-900 dark:via-slate-900 dark:to-slate-950">
          <div className="flex flex-col gap-6 xl:flex-row xl:items-start xl:justify-between">
            <div>
              <div className="inline-flex items-center rounded-full border border-slate-200 bg-white px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500 shadow-sm dark:border-slate-700 dark:bg-slate-800/70 dark:text-slate-400">
                Operations Workspace
              </div>

              <h1 className="mt-4 text-3xl font-semibold tracking-tight text-slate-900 dark:text-white">
                Task Management
              </h1>

              <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-500 dark:text-slate-400">
                A central operational view for task assignment, field execution,
                technician workload visibility, and service follow-through.
              </p>
            </div>

            <div className="flex flex-col gap-3 sm:flex-row">
              <button
                type="button"
                onClick={() => loadTasks(statusFilter)}
                className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-300 bg-white px-4 py-3 text-sm font-medium text-slate-700 transition hover:bg-slate-100 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
              >
                ↻ Refresh
              </button>

              <button
                onClick={() => setOpenAddTask(true)}
                className="inline-flex items-center justify-center gap-2 rounded-2xl bg-black px-5 py-3 text-sm font-semibold text-white transition hover:opacity-90 dark:bg-white dark:text-black"
              >
                <span className="text-base leading-none">＋</span>
                Create Task
              </button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3 p-6 md:grid-cols-3 xl:grid-cols-6">
          <KpiCard label="Total Tasks" value={stats.total} icon="🗂️" subtitle="All tracked work items" />
          <KpiCard label="Pending" value={stats.pending} icon="⏳" tone="amber" subtitle="Awaiting acceptance" />
          <KpiCard label="In Progress" value={stats.inProgress} icon="⚙️" tone="blue" subtitle="Currently being worked" />
          <KpiCard label="Completed" value={stats.completed} icon="✅" tone="emerald" subtitle="Successfully closed" />
          <KpiCard label="Overdue" value={stats.overdue} icon="🚨" tone="red" subtitle="Past target date" />
          <KpiCard label="Unassigned" value={stats.unassigned} icon="👨‍🔧" subtitle="Require technician allocation" />
        </div>
      </section>

      {/* Filters and controls */}
      <section className="rounded-3xl border border-slate-200 bg-white p-4 shadow-[0_10px_35px_rgba(15,23,42,0.06)] dark:border-slate-800 dark:bg-slate-900 dark:shadow-[0_10px_35px_rgba(0,0,0,0.28)]">
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
            <div className="flex flex-1 flex-col gap-3 lg:flex-row">
              <div className="relative flex-1">
                <span className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500">
                  🔎
                </span>
                <input
                  type="text"
                  placeholder="Search by task ID, title, customer, technician, tractor, serial..."
                  className="w-full rounded-2xl border border-slate-300 bg-white py-3 pl-11 pr-4 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-slate-900 focus:ring-4 focus:ring-slate-200 dark:border-slate-700 dark:bg-slate-900 dark:text-white dark:placeholder:text-slate-500 dark:focus:border-white dark:focus:ring-slate-800"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>

              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="min-w-[220px] rounded-2xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-slate-900 focus:ring-4 focus:ring-slate-200 dark:border-slate-700 dark:bg-slate-900 dark:text-white dark:focus:border-white dark:focus:ring-slate-800"
              >
                {STATUS_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>

              <select
                value={pageSize}
                onChange={(e) => setPageSize(Number(e.target.value))}
                className="min-w-[150px] rounded-2xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-slate-900 focus:ring-4 focus:ring-slate-200 dark:border-slate-700 dark:bg-slate-900 dark:text-white dark:focus:border-white dark:focus:ring-slate-800"
              >
                {PAGE_SIZE_OPTIONS.map((size) => (
                  <option key={size} value={size}>
                    {size} / page
                  </option>
                ))}
              </select>
            </div>

            <button
              type="button"
              onClick={() => {
                setSearch("");
                setStatusFilter("All");
                setPage(1);
              }}
              className="inline-flex items-center justify-center rounded-2xl border border-slate-300 bg-white px-4 py-3 text-sm font-medium text-slate-700 transition hover:bg-slate-100 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
            >
              Clear Filters
            </button>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {STATUS_OPTIONS.map((option) => {
              const active = statusFilter === option.value;
              return (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => setStatusFilter(option.value)}
                  className={`rounded-full px-4 py-2 text-sm font-medium transition ${
                    active
                      ? "bg-black text-white shadow-sm dark:bg-white dark:text-black"
                      : "bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
                  }`}
                >
                  {option.label}
                </button>
              );
            })}
          </div>

          <div className="flex flex-wrap items-center gap-2 border-t border-slate-200 pt-4 dark:border-slate-800">
            {search.trim() && (
              <span className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1.5 text-xs font-medium text-slate-700 dark:bg-slate-800 dark:text-slate-200">
                Search: “{search}”
              </span>
            )}

            <span className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1.5 text-xs font-medium text-slate-700 dark:bg-slate-800 dark:text-slate-200">
              Status: {activeFilterLabel}
            </span>

            <span className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1.5 text-xs font-medium text-slate-700 dark:bg-slate-800 dark:text-slate-200">
              Records: {filteredTasks.length}
            </span>
          </div>
        </div>
      </section>

      {loading ? (
        <TableLoader
          columns={7}
          rows={8}
          showSearch={false}
          showAction={false}
          showTabs={false}
        />
      ) : filteredTasks.length === 0 ? (
        <div className="rounded-3xl border border-slate-200 bg-white px-6 py-20 text-center shadow-[0_10px_35px_rgba(15,23,42,0.06)] dark:border-slate-800 dark:bg-slate-900 dark:shadow-[0_10px_35px_rgba(0,0,0,0.28)]">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-slate-100 text-2xl dark:bg-slate-800">
            🗂️
          </div>

          <h3 className="mt-5 text-lg font-semibold text-slate-900 dark:text-white">
            No tasks found
          </h3>

          <p className="mx-auto mt-2 max-w-md text-sm text-slate-500 dark:text-slate-400">
            {search || statusFilter !== "All"
              ? "No tasks match your current search or filter selection. Try refining your query or resetting filters."
              : "There are no tasks yet. Create the first one to begin managing field operations."}
          </p>

          {!search && statusFilter === "All" && (
            <button
              onClick={() => setOpenAddTask(true)}
              className="mt-6 inline-flex items-center justify-center gap-2 rounded-2xl bg-black px-5 py-3 text-sm font-semibold text-white transition hover:opacity-90 dark:bg-white dark:text-black"
            >
              <span className="text-base leading-none">＋</span>
              Create your first task
            </button>
          )}
        </div>
      ) : (
        <section className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-[0_10px_35px_rgba(15,23,42,0.06)] dark:border-slate-800 dark:bg-slate-900 dark:shadow-[0_10px_35px_rgba(0,0,0,0.28)]">
          <div className="flex flex-col gap-3 border-b border-slate-200 px-6 py-4 sm:flex-row sm:items-center sm:justify-between dark:border-slate-800">
            <div>
              <h2 className="text-lg font-semibold text-slate-900 dark:text-white">
                Task Register
              </h2>
              <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                Showing {paginatedTasks.length} of {filteredTasks.length} matching task
                {filteredTasks.length === 1 ? "" : "s"}.
              </p>
            </div>

            <div className="rounded-full bg-slate-100 px-3 py-1.5 text-xs font-semibold uppercase tracking-[0.14em] text-slate-500 dark:bg-slate-800 dark:text-slate-300">
              Page {safePage} of {totalPages}
            </div>
          </div>

          <div className="hidden overflow-x-auto xl:block">
            <table className="w-full min-w-[1260px]">
              <thead className="bg-slate-50/80 dark:bg-slate-950/30">
                <tr className="border-b border-slate-200 dark:border-slate-800">
                  <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">
                    Task
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">
                    Status
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">
                    Technician
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">
                    Customer
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">
                    Tractor
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">
                    Timeline
                  </th>
                  <th className="px-6 py-4 text-right text-xs font-semibold uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">
                    Actions
                  </th>
                </tr>
              </thead>

              <tbody>
                {paginatedTasks.map((task) => {
                  const unassigned = task.technician?.name === "Unassigned";

                  return (
                    <tr
                      key={task.id}
                      className="border-b border-slate-200 align-top transition hover:bg-slate-50/70 dark:border-slate-800 dark:hover:bg-slate-800/30"
                    >
                      <td className="px-6 py-5">
                        <div className="flex items-start gap-3">
                          <div className="mt-0.5 flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-slate-100 text-sm font-semibold text-slate-700 dark:bg-slate-800 dark:text-slate-300">
                            #
                          </div>

                          <div className="min-w-0">
                            <p className="text-sm font-semibold text-slate-900 dark:text-white">
                              {task.title}
                            </p>
                            <p className="mt-1 text-xs font-medium text-slate-500 dark:text-slate-400">
                              ID: {task.id}
                            </p>
                            <p className="mt-2 line-clamp-2 max-w-md text-sm leading-6 text-slate-500 dark:text-slate-400">
                              {task.description || "No description available."}
                            </p>
                          </div>
                        </div>
                      </td>

                      <td className="px-6 py-5">
                        <span
                          className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-wide ${statusStyles(
                            task.status
                          )}`}
                        >
                          {prettyStatus(task.status)}
                        </span>
                      </td>

                      <td className="px-6 py-5">
                        {unassigned ? (
                          <span className="inline-flex rounded-full bg-red-50 px-3 py-1 text-xs font-semibold text-red-700 ring-1 ring-red-200 dark:bg-red-500/10 dark:text-red-300 dark:ring-red-500/20">
                            Unassigned
                          </span>
                        ) : (
                          <div>
                            <p className="text-sm font-medium text-slate-900 dark:text-white">
                              {task.technician?.name}
                            </p>
                            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                              Assigned technician
                            </p>
                          </div>
                        )}
                      </td>

                      <td className="px-6 py-5">
                        <p className="text-sm font-medium text-slate-900 dark:text-white">
                          {task.customer?.name}
                        </p>
                        <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                          Customer account
                        </p>
                      </td>

                      <td className="px-6 py-5">
                        <p className="text-sm font-medium text-slate-900 dark:text-white">
                          {task.tractor?.model || "—"}
                        </p>
                        <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                          Serial: {task.tractor?.serial || "—"}
                        </p>
                      </td>

                      <td className="px-6 py-5">
                        <div className="space-y-2 text-sm">
                          <div>
                            <span className="text-slate-500 dark:text-slate-400">
                              Assigned:
                            </span>{" "}
                            <span className="font-medium text-slate-900 dark:text-white">
                              {formatDate(task.assignedDate)}
                            </span>
                          </div>
                          <div>
                            <span className="text-slate-500 dark:text-slate-400">
                              Due:
                            </span>{" "}
                            <span className="font-medium text-slate-900 dark:text-white">
                              {formatDate(task.dueDate)}
                            </span>
                          </div>
                        </div>
                      </td>

                      <td className="px-6 py-5">
                        <div className="flex justify-end gap-2">
                          {unassigned && (
                            <button
                              onClick={() => setAssignTaskId(task.id)}
                              className="rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-100 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
                            >
                              Assign
                            </button>
                          )}

                          <button
                            onClick={() => navigate(`/tasks/${task.id}`)}
                            className="rounded-xl bg-black px-4 py-2 text-sm font-semibold text-white transition hover:opacity-90 dark:bg-white dark:text-black"
                          >
                            View Task
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="space-y-4 p-4 xl:hidden">
            {paginatedTasks.map((task) => {
              const unassigned = task.technician?.name === "Unassigned";

              return (
                <div
                  key={task.id}
                  className="rounded-2xl border border-slate-200 bg-slate-50/50 p-4 dark:border-slate-800 dark:bg-slate-950/30"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">
                        ID {task.id}
                      </p>
                      <h3 className="mt-1 text-base font-semibold text-slate-900 dark:text-white">
                        {task.title}
                      </h3>
                    </div>

                    <span
                      className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-wide ${statusStyles(
                        task.status
                      )}`}
                    >
                      {prettyStatus(task.status)}
                    </span>
                  </div>

                  <p className="mt-3 text-sm leading-6 text-slate-500 dark:text-slate-400">
                    {task.description || "No description available."}
                  </p>

                  <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <div className="rounded-xl bg-white p-3 ring-1 ring-slate-200 dark:bg-slate-900 dark:ring-slate-800">
                      <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">
                        Technician
                      </p>
                      <p className="mt-1 text-sm font-medium text-slate-900 dark:text-white">
                        {unassigned ? "Unassigned" : task.technician?.name}
                      </p>
                    </div>

                    <div className="rounded-xl bg-white p-3 ring-1 ring-slate-200 dark:bg-slate-900 dark:ring-slate-800">
                      <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">
                        Customer
                      </p>
                      <p className="mt-1 text-sm font-medium text-slate-900 dark:text-white">
                        {task.customer?.name}
                      </p>
                    </div>

                    <div className="rounded-xl bg-white p-3 ring-1 ring-slate-200 dark:bg-slate-900 dark:ring-slate-800">
                      <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">
                        Tractor
                      </p>
                      <p className="mt-1 text-sm font-medium text-slate-900 dark:text-white">
                        {task.tractor?.model || "—"}
                      </p>
                      <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                        {task.tractor?.serial || "—"}
                      </p>
                    </div>

                    <div className="rounded-xl bg-white p-3 ring-1 ring-slate-200 dark:bg-slate-900 dark:ring-slate-800">
                      <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">
                        Timeline
                      </p>
                      <p className="mt-1 text-sm font-medium text-slate-900 dark:text-white">
                        {formatDate(task.assignedDate)}
                      </p>
                      <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                        Due {formatDate(task.dueDate)}
                      </p>
                    </div>
                  </div>

                  <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:justify-end">
                    {unassigned && (
                      <button
                        onClick={() => setAssignTaskId(task.id)}
                        className="rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 transition hover:bg-slate-100 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
                      >
                        Assign Technician
                      </button>
                    )}

                    <button
                      onClick={() => navigate(`/tasks/${task.id}`)}
                      className="rounded-xl bg-black px-4 py-2.5 text-sm font-semibold text-white transition hover:opacity-90 dark:bg-white dark:text-black"
                    >
                      View Task
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="flex flex-col gap-4 border-t border-slate-200 px-6 py-4 sm:flex-row sm:items-center sm:justify-between dark:border-slate-800">
            <div className="text-sm text-slate-500 dark:text-slate-400">
              Showing{" "}
              <span className="font-semibold text-slate-900 dark:text-white">
                {filteredTasks.length === 0 ? 0 : (safePage - 1) * pageSize + 1}
              </span>{" "}
              to{" "}
              <span className="font-semibold text-slate-900 dark:text-white">
                {Math.min(safePage * pageSize, filteredTasks.length)}
              </span>{" "}
              of{" "}
              <span className="font-semibold text-slate-900 dark:text-white">
                {filteredTasks.length}
              </span>{" "}
              tasks
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={safePage === 1}
                className="rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
              >
                Previous
              </button>

              {visiblePageNumbers.map((item, index) =>
                item === "..." ? (
                  <span
                    key={`ellipsis-${index}`}
                    className="px-2 text-sm text-slate-400 dark:text-slate-500"
                  >
                    ...
                  </span>
                ) : (
                  <button
                    key={item}
                    type="button"
                    onClick={() => setPage(item)}
                    className={`min-w-[40px] rounded-xl px-3 py-2 text-sm font-semibold transition ${
                      safePage === item
                        ? "bg-black text-white dark:bg-white dark:text-black"
                        : "border border-slate-300 bg-white text-slate-700 hover:bg-slate-100 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
                    }`}
                  >
                    {item}
                  </button>
                )
              )}

              <button
                type="button"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={safePage === totalPages}
                className="rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
              >
                Next
              </button>
            </div>
          </div>
        </section>
      )}

      <AddTaskModal
        open={openAddTask}
        onClose={() => {
          setOpenAddTask(false);
          loadTasks(statusFilter);
        }}
      />

      <AssignTechnicianModal
        open={!!assignTaskId}
        taskId={assignTaskId}
        technicians={technicians}
        onClose={() => setAssignTaskId(null)}
        onAssigned={() => loadTasks(statusFilter)}
      />
    </div>
  );
}